class gTagEvents {
    constructor() {
        this.delegateBody = new Delegate(document.body);
        this.lastAddToCartButtonId = "";
        this.lastAddToCartContext = "";
        this.lastAddToCartAt = 0;

        // Track which ATC button initiated the latest add-to-cart.
        // We use capture phase so we record it even if other code stops propagation.
        document.addEventListener(
          "click",
          (event) => {
            const button = event.target?.closest?.('button, input[type="submit"]');
            const atc = event.target?.closest?.('[data-action="add-to-cart"], [data-product-add-to-cart-button], [name="add"]');
            if (!button && !atc) return;

            const atcEl = atc || button;
            const inUpsell = !!atcEl?.closest?.(".upsell__block");
            const inMiniCartRecommendations = !!atcEl?.closest?.(".mini-cart__recommendations");

            // Sticky ATC button has a stable id. Record it explicitly.
            if (button?.id === "StickyAddToCart") {
              this.lastAddToCartButtonId = "StickyAddToCart";
              this.lastAddToCartContext = "sticky";
              this.lastAddToCartAt = Date.now();
              return;
            }

            // Also record other ATC-like buttons so the sticky marker doesn't "stick" forever.
            if (
              atcEl?.matches?.('[data-product-add-to-cart-button], [data-action="add-to-cart"], [name="add"]') ||
              atcEl?.getAttribute?.("type") === "submit"
            ) {
              this.lastAddToCartButtonId = button?.id || "";
              this.lastAddToCartContext = inMiniCartRecommendations ? "mini_cart_recommendations" : inUpsell ? "upsell" : "";
              this.lastAddToCartAt = Date.now();
            }
          },
          true
        );

        this.initIntersectionObserver();
        this.interceptCartApi();
        this.bindEvents();
        this.observeDOMChanges();
        this.domChanged();
    }

    bindEvents() {
        this.delegateBody.on("filters:sort:changed", () => { this.sendSortByData(); this.sendFiltersData(); });
        this.delegateBody.on("click", '[name="checkout"]', this.handleCheckout.bind(this));
        this.delegateBody.on("filters:cleared", this.clearFiltersData.bind(this));
        this.delegateBody.on("facet:criteria-changed", this.sendSortByData.bind(this));
        this.delegateBody.on("click", '[data-action="ga-send-select"], [data-ga-action="ga-send-select"]', this.handleSelectItem.bind(this));
        this.delegateBody.on("click", ".cartFooter--bottomLink", this.handleCartContinueShopping.bind(this));
        this.delegateBody.on("click", ".metaButtons--wrapper .metaButton", this.handleCollectionPageClick.bind(this));
        this.delegateBody.on("click", ".product__info-headings .swiper-slide", this.handleProductPageClick.bind(this));
        this.delegateBody.on("click", "div[oke-quiz] .c-startPanel-button", this.handleQuizStartClick.bind(this));
        this.delegateBody.on("input", "div[oke-quiz] c-emailInput input[type=\"email\"]", this.handleQuizEmailStepClick.bind(this));
        this.delegateBody.on("click", "div[oke-quiz] button.c-questionsForm-next--email", this.handleQuizCompletionClick.bind(this));
        // Covers pressing Enter in the email field (form submit), not just clicking the submit button.
        this.delegateBody.on("submit", "div[oke-quiz] form", this.handleQuizCompletionSubmit.bind(this));
        this.delegateBody.on("click", ".product__media", this.handleProductImageGalleryClick.bind(this));
        this.delegateBody.on("touchstart", ".product__media .flickity-slider", this.handleProductImageSwipeTouchStart.bind(this));
        this.delegateBody.on("click", "div[oke-star-rating]", this.handleProductRatingsInteraction.bind(this));
        this.delegateBody.on("click", ".connected-items__wrapper a.SelectboxLabelNormal[data-title]", this.handleProductSizeClick.bind(this));
        this.delegateBody.on("click", ".SelectBox--WrapperColor a.SelectboxCircle[data-title], .SelectBox--InnerWrapperColor a.SelectboxCircle[data-title]", this.handleProductColorClick.bind(this));
        this.delegateBody.on("click", ".productInfo--wraper .quiz--button", this.handleProductQuizCTAClick.bind(this));
        this.delegateBody.on("click", ".product__info-wrapper .swiper-slide", this.handleProductTabsClick.bind(this));
        this.delegateBody.on("click", ".Product--ExpandButton", this.handleProductViewAllIngredientsClick.bind(this));
        this.delegateBody.on("click", "#section-firework-embed, .section-firework-embed, section-firework-embed", this.handleProductVideoClick.bind(this));
        this.delegateBody.on("click", ".oke-w-navBar-item", this.handleProductReviewsTabClick.bind(this));
        this.delegateBody.on("click", ".upsell__block .upsell__tab", this.handleProductRecommendedCarouselTabClick.bind(this));
        this.delegateBody.on("click", ".mini-cart__recommendations .swiperButton", this.handleCartRecommendedCarouselArrowsClick.bind(this));
    }

    initIntersectionObserver() {
      const settings =  {
        root: null, // Use the viewport as the root
        rootMargin: "0px",
        threshold: 0.1, // Adjust the threshold as per your requirement
      }

      this.intersectionObserver = new IntersectionObserver(this.handleIntersection.bind(this), settings);
    }

    sendSortByData(){
        const isDesktop =  new Responsive().matchesBreakpoint('lap-and-up');
        const oldSortByValue = sessionStorage.getItem("SortByValue");
        const sortBy = isDesktop ? 
            document.querySelector('input[name="sort_by"]:checked') : 
            document.querySelector('#sortby.Drawer--wrapper input[name="sort_by"]:checked');    

        if (!sortBy) return;
        const sortByValue = isDesktop ? 
            sortBy.getAttribute('title').replace(' ', '-') : 
            sortBy.value;

        if (!sortByValue || sortByValue === "" || oldSortByValue === sortByValue) return;
        sessionStorage.setItem("SortByValue", sortByValue);

        const payload = {
            event: "sort_applied",
            sort_by: sortByValue,
        };
        dataLayer.push(payload);
    }

    sendFiltersData() {  
        function arraysEqual(arr1, arr2) {
            if (!arr1 || !arr2) return arr1 === arr2;

            return (
                Array.isArray(arr1) &&
                Array.isArray(arr2) &&
                arr1.length === arr2.length &&
                arr1.every((val, index) => val === arr2[index])
            );
        }

        const filtersList = [];
        let oldFiltersList = sessionStorage.getItem("CollectionsFiltersList");
        const filterCheckboxes = document.querySelectorAll('.inputFilterCheckbox');

        if (oldFiltersList) oldFiltersList = JSON.parse(oldFiltersList);

        filterCheckboxes.forEach(checkbox => {
            if (checkbox.checked) {
                filtersList.push(checkbox.dataset.filterName + ":" + checkbox.value);
            }
        });
        
        if (!filtersList.length || arraysEqual(filtersList, oldFiltersList)) return;

        sessionStorage.setItem("CollectionsFiltersList", JSON.stringify(filtersList));

        const payload = {
            event: "filter_applied",
            filter_by: filtersList,
        };
        dataLayer.push(payload);
    }

    clearFiltersData() {
        // Get the previously stored filters before clearing
        let oldFiltersList = sessionStorage.getItem("CollectionsFiltersList");
        if (oldFiltersList) {
            oldFiltersList = JSON.parse(oldFiltersList);
        } else {
            oldFiltersList = [];
        }
        
        // Clear the stored filters
        sessionStorage.setItem("CollectionsFiltersList", JSON.stringify([]));
        
        const payload = {
            event: "clear_filters",
            filter_by: oldFiltersList,
        };
        dataLayer.push(payload);
    }

    interceptCartApi() {
        const originalFetch = window.fetch;
        const _this = this;

        function fetchUrlString(input) {
            if (typeof input === "string") return input;
            if (typeof URL !== "undefined" && input instanceof URL) return input.href;
            if (typeof Request !== "undefined" && input instanceof Request) return input.url;
            return "";
        }

        window.fetch = async function (...args) {
            const [input, init] = args;
            const urlString = fetchUrlString(input);

            // Only wrap Shopify cart AJAX calls — not arbitrary *.js assets (avoids clone/json and errors on non-JSON).
            if (!urlString.includes("/cart")) {
                return originalFetch.apply(this, args);
            }

            if (urlString.includes(`/cart/clear`)) _this.cartClear();
            if (urlString.includes(`/cart/update`)) _this.cartUpdate(init && init.body);

            // Let network failures reject like native fetch (no catch/retry — avoids duplicate requests and misleading logs).
            const response = await originalFetch.apply(this, args);
            if (!response.ok) return response;
            if (urlString === "" || !dataLayer) return response;

            try {
                if (urlString.includes(`/cart/add`)) {
                    const responseClone = response.clone();
                    const json = await responseClone.json();
                    _this.cartAdd(json, init);
                } else if (urlString.includes(`/cart/change`)) {
                    const responseClone = response.clone();
                    const json = await responseClone.json();
                    _this.cartChange(json);
                }
            } catch (e) {
                console.error("GTM cart response handling error:", e);
            }

            return response;
        };
    }

    async getCart() {
        const url = (window.themeVariables && window.themeVariables.routes && window.themeVariables.routes.rootUrlWithoutSlash)
            ? `${window.themeVariables.routes.rootUrlWithoutSlash}/cart.js`
            : `${window.location.origin}/cart.js`;
        const response = await fetch(url);
        if (!response.ok) return { items: [] };
        return response.json();
    }

    async cartAdd(cart, request) {
        let cartItems = cart.items ? cart.items : [cart];
        cartItems = this.getDataFromRequest(cartItems, request);
        this.productAdded(cartItems);
    }

    async cartUpdate(body) {
      if (!body) return;
      
      let parsedBody;
      try {
        parsedBody = JSON.parse(body);
      } catch (e) {
        console.error('[GTag] Error parsing cart update body:', e);
        return;
      }
      
      const updates = parsedBody?.updates;
      if (!updates || typeof updates !== 'object') {
        console.warn('[GTag] No updates object found in cart update body');
        return;
      }
  
      const oldCart = await this.getCart();
      if (!oldCart || !oldCart.items || !Array.isArray(oldCart.items)) {
        console.warn('[GTag] Invalid cart data received');
        return;
      }
      
      const cartItems = oldCart.items;
      let itemsAdded = [], itemsRemoved = [];

      cartItems.forEach(item => {
          if (!item || !item.key) return;
          
          const newQuantity = updates[item.key];
          if (typeof newQuantity === "number") {
              const change = newQuantity - item.quantity;
              item.quantity = Math.abs(change);
              change < 1 ? itemsRemoved.push(item) : itemsAdded.push(item);
          }
      });

      itemsAdded = this.setLocationShoppingCart(itemsAdded);
      itemsRemoved = this.setLocationShoppingCart(itemsRemoved);

      this.productAdded(itemsAdded);
      this.productsRemoved(itemsRemoved);
    }

    async cartChange(cart) {      
      const itemsAdded = this.setLocationShoppingCart(cart.items_added);
      const itemsRemoved = this.setLocationShoppingCart(cart.items_removed);

      this.productAdded(itemsAdded);
      this.productsRemoved(itemsRemoved);
    }

    productAdded(cartItems) {
        if (!cartItems || !cartItems.length) return;

        const ecommerce = this.buildEcommerce(cartItems);

        const customEvent = {
            event: 'custom_add_to_cart',
            ecommerce
        };

        dataLayer.push({ ecommerce: null });
        dataLayer.push(customEvent);
        console.log("[GTag] custom_add_to_cart", customEvent);

        // If the StickyAddToCart button initiated this add, also fire a dedicated event
        // with the exact same ecommerce payload (same as custom_add_to_cart).
        const now = Date.now();
        if (this.lastAddToCartButtonId === "StickyAddToCart" && now - this.lastAddToCartAt < 15000) {
          const stickyEvent = { event: "product_sticky_add_to_cart", ecommerce };
          dataLayer.push({ ecommerce: null });
          dataLayer.push(stickyEvent);
          console.log("[GTag] product_sticky_add_to_cart", stickyEvent);
        }

        if (this.lastAddToCartContext === "upsell" && now - this.lastAddToCartAt < 15000) {
          const upsellEvent = { event: "product_recommended_carousel_add_to_cart", ecommerce };
          dataLayer.push({ ecommerce: null });
          dataLayer.push(upsellEvent);
          console.log("[GTag] product_recommended_carousel_add_to_cart", upsellEvent);
        }

        if (this.lastAddToCartContext === "mini_cart_recommendations" && now - this.lastAddToCartAt < 15000) {
          const miniCartEvent = { event: "cart_recommended_carousel_add_to_cart", ecommerce };
          dataLayer.push({ ecommerce: null });
          dataLayer.push(miniCartEvent);
          console.log("[GTag] cart_recommended_carousel_add_to_cart", miniCartEvent);
        }

        // Derive collection context from the current URL (only fire on collection pages)
        const collectionContext = this.getCurrentCollectionContext();

        if (collectionContext) {
          const collectionsEvent = {
            event: 'collections_add_to_cart',
            ecommerce,
            collection_handle: collectionContext.handle,
            collection_url: collectionContext.url
          };

          dataLayer.push({ ecommerce: null });
          dataLayer.push(collectionsEvent);
        }
    }

    getCurrentCollectionContext() {
      try {
        const path = window.location.pathname || "";
        const match = path.match(/\/collections\/([^\/\?]+)/);
        if (!match) return null;

        const handle = match[1];
        const base = window.location.origin || "";

        return {
          handle,
          url: `${base}/collections/${handle}`
        };
      } catch (e) {
        console.error("[GTag] Error detecting collection context:", e);
        return null;
      }
    }

    productsRemoved(cartItems) {
      if (!cartItems || !cartItems.length) return;

      const customEvent = {
            'event': "custom_remove_from_cart",
            'ecommerce': this.buildEcommerce(cartItems)
      };

      dataLayer.push({ ecommerce: null });
      dataLayer.push(customEvent);
    }

    buildEcommerce(cartItems) {
        const items = [];
        let totalPrice = 0;        
        const global_item_id = cartItems[0].list_id;
        const global_item_name = cartItems[0].list_name;
        cartItems.forEach((item) => {            
            const price = typeof item.price === "number" ? item.price / 100 : Number(item.price);
            totalPrice += price * item.quantity;
            
            const product = {
                'item_id': item.sku || "",
                'item_name': item.untranslated_product_title || item.title || "",
                'item_variant': item.untranslated_variant_title || "Default Title",
                'price': price, 
                'quantity': item.quantity,
                'category': item["category"] || "",
                'category2': item["category2"] || "",
                'item_brand': item["item_brand"] || item.vendor || "",
                'item_list_id': item["list_id"] || "",
                'item_list_name': item["list_name"] || ""
            };

            items.push(product);
        });

        return {
            'value': totalPrice,
            'currency': ShopifyAnalytics.meta.currency,
            'item_list_id':global_item_id,
            'item_list_name': global_item_name,
            'items': items
        };
    }

    getDataFromRequest(cartItems, request) {
        const body = request?.body || null;
        if (!body) return cartItems;

        const json = body instanceof FormData
            ? Object.fromEntries(body.entries())
            : JSON.parse(body);

        const quantity = json["quantity"] || 1;
        cartItems[0].quantity = Number(quantity);
        
        cartItems[0]["category"] = json["_gtm-category"] || "";
        cartItems[0]["category2"] = json["_gtm-category2"] || "";
        cartItems[0]["item_brand"] = json["_gtm-item_brand"] || "";
        cartItems[0]["list_id"] = json["_gtm-list_id"] || "";
        cartItems[0]["list_name"] = json["_gtm-list_name"] || "";

        return cartItems;
    }

    setLocationShoppingCart(array) {
      return array.map(item => {            
          item["list_id"] = "shopping_cart"
          item["list_name"] = "Shopping cart"

          // Match DOM: Liquid uses item.key for data-gtm-id; cart API uses key
          const lineItemKey = item.key || item.view_key;
          const lineItem = lineItemKey ? document.querySelector(`[data-gtm-id="${lineItemKey}"]`) : null;
          if (lineItem) {
            const category = lineItem.dataset.gtmCategory;
            const category2 = lineItem.dataset.gtmCategory2;
            const brand = lineItem.dataset.gtmItemBrand;
            if (category) item["category"] = category;
            if (category2) item["category2"] = category2;
            if (brand) item["item_brand"] = brand;
          }
          // Fallback: Shopify cart API includes vendor on each item
          if (!item["item_brand"] && item.vendor) item["item_brand"] = item.vendor;

          return item;
      });
    }

    handleCheckout(event) {
        const button = event.target.closest("button[name='checkout']");
        if (!button) return;

        const script = button.parentElement.querySelector('[type="application/ld+json"]');
        const settings = JSON.parse(script.innerText);

        if (!dataLayer || !settings) return;

        dataLayer.push({ ecommerce: null });
        dataLayer.push(settings);

    }

    handleSelectItem(event) {
      let target = event.target.closest('[data-action="ga-send-select"]') || event.target.closest('[data-ga-action="ga-send-select"]');
      if (!target || !dataLayer) return;

      const settings = JSON.parse(target.dataset.selectSettings);
      if (!settings) return;

      const customEvent = { 
          event: "custom_select_item",
          ecommerce: {
              item_list_id: settings.list_id,
              item_list_name: settings.list_name,
              currency: ShopifyAnalytics.meta.currency,
              items: [settings]
          }
      }
  
      dataLayer.push({ ecommerce: null });
      dataLayer.push(customEvent);
  }

    handleCartContinueShopping(event) {
      if (!dataLayer) return;

      const payload = {
        event: "cart_continue_shopping",
        link_url: event.currentTarget?.href || window.location.href
      };

      dataLayer.push(payload);
    }

    handleCollectionPageClick(event) {
      if (!dataLayer) return;

      const button = event.target.closest(".metaButtons--wrapper .metaButton");
      if (!button) return;

      // Purple button contains the module title as the span text (see collection section markup).
      const moduleTitle =
        button.querySelector("span")?.textContent?.trim() ||
        button.textContent?.trim() ||
        "";

      if (!moduleTitle) return;

      // Only fire on real collection pages.
      const collectionContext = this.getCurrentCollectionContext();
      if (!collectionContext) return;

      const payload = {
        event: "collection_page_click",
        module_title: moduleTitle,
        // GA4 dimension mapping expects `module_distributor` as well.
        module_distributor: moduleTitle,
      };

      // Debug: verify payload sent to dataLayer.
      console.log("[GTag] collection_page_click", payload);
      dataLayer.push(payload);
    }

    handleProductPageClick(event) {
      if (!dataLayer) return;

      const slide = event.target.closest(".product__info-headings .swiper-slide");
      if (!slide) return;

      const moduleTitle =
        slide.querySelector("span")?.textContent?.trim() ||
        slide.textContent?.trim() ||
        "";

      if (!moduleTitle) return;

      // Only fire on product pages.
      const path = window.location.pathname || "";
      const match = path.match(/\/products\/([^\/\?\#]+)/);
      if (!match) return;

      const payload = {
        event: "product_page_click",
        tab_title: moduleTitle,
        product_handle: match[1],
      };

      console.log("[GTag] product_page_click", payload);
      dataLayer.push(payload);
    }

    handleQuizStartClick(event) {
      if (!dataLayer) return;

      const payload = {
        event: "quiz_start",
      };

      // Debug: verify payload sent to dataLayer.
      console.log("[GTag] quiz_start", payload);
      dataLayer.push(payload);
    }

    handleQuizEmailStepClick(event) {
      if (!dataLayer) return;

      const input = event.target;
      if (!input || input.tagName !== "INPUT") return;

      // Fire only once per input element, and only when the user has started typing.
      if (input.dataset.gtmQuizEmailStepFired === "true") return;
      if (!input.value || input.value.trim() === "") return;

      input.dataset.gtmQuizEmailStepFired = "true";

      const payload = { event: "quiz_email_step" };

      // Debug: verify payload sent to dataLayer.
      console.log("[GTag] quiz_email_step", payload);
      dataLayer.push(payload);
    }

    handleQuizCompletionClick(event) {
      if (!dataLayer) return;

      const quizRoot = event.target?.closest?.("div[oke-quiz]");
      if (quizRoot) quizRoot.dataset.gtmQuizCompletionFired = "true";

      const payload = { event: "quiz_completion" };

      // Debug: verify payload sent to dataLayer.
      console.log("[GTag] quiz_completion", payload);
      dataLayer.push(payload);
    }

    handleQuizCompletionSubmit(event) {
      if (!dataLayer) return;

      const form = event.target;
      if (!form || !(form instanceof HTMLFormElement)) return;

      const quizRoot = form.closest("div[oke-quiz]");
      if (!quizRoot) return;

      // Dedupe: avoid firing twice when click triggers submit.
      if (quizRoot.dataset.gtmQuizCompletionFired === "true") return;

      const nextButton = quizRoot.querySelector("button.c-questionsForm-next--email");
      if (!nextButton) return;

      const emailInput = quizRoot.querySelector('c-emailInput input[type="email"]');
      if (!emailInput) return;

      if (!emailInput.value || emailInput.value.trim() === "") return;

      quizRoot.dataset.gtmQuizCompletionFired = "true";

      const payload = { event: "quiz_completion" };

      // Debug: verify payload sent to dataLayer.
      console.log("[GTag] quiz_completion", payload);
      dataLayer.push(payload);
    }

    handleProductImageGalleryClick(event) {
      if (!dataLayer) return;

      const isDesktop = new Responsive().matchesBreakpoint("lap-and-up");
      if (!isDesktop) return;

      const media = event.target.closest(".product__media");
      if (!media) return;

      const payload = { event: "product_image_gallery" };
      console.log("[GTag] product_image_gallery", payload);
      dataLayer.push(payload);
    }

    handleProductImageSwipeTouchStart(event) {
      if (!dataLayer) return;

      const isDesktop = new Responsive().matchesBreakpoint("lap-and-up");
      if (isDesktop) return;

      // Only fire on product pages.
      const path = window.location.pathname || "";
      const match = path.match(/\/products\/([^\/\?\#]+)/);
      if (!match) return;

      const slider = event.target?.closest?.(".flickity-slider");
      if (!slider) return;

      // Dedupe quick repeated touchstarts (some browsers can emit multiple).
      const now = Date.now();
      const last = Number(slider.dataset.gtmProductImageSwipeLastTouchStart || 0);
      if (now - last < 600) return;
      slider.dataset.gtmProductImageSwipeLastTouchStart = String(now);

      const payload = { event: "product_image_swipe" };
      console.log("[GTag] product_image_swipe", payload);
      dataLayer.push(payload);
    }

    handleProductRatingsInteraction(event) {
      if (!dataLayer) return;

      const rating = event.target?.closest?.("div[oke-star-rating]");
      if (!rating) return;

      // Only fire on product pages.
      const path = window.location.pathname || "";
      const match = path.match(/\/products\/([^\/\?\#]+)/);
      if (!match) return;

      const payload = { event: "product_ratings" };
      console.log("[GTag] product_ratings", payload);
      dataLayer.push(payload);
    }

    handleProductSizeClick(event) {
      if (!dataLayer) return;

      const link = event.target?.closest?.('.connected-items__wrapper a.SelectboxLabelNormal[data-title]');
      if (!link) return;

      // Only fire on product pages.
      const path = window.location.pathname || "";
      const match = path.match(/\/products\/([^\/\?\#]+)/);
      if (!match) return;

      const title = link.dataset.title?.trim() || "";
      if (!title) return;

      const payload = {
        event: "product_size",
        size_title: title,
      };

      console.log("[GTag] product_size", payload);
      dataLayer.push(payload);
    }

    handleProductColorClick(event) {
      if (!dataLayer) return;

      const swatch = event.target?.closest?.(
        ".SelectBox--WrapperColor a.SelectboxCircle[data-title], .SelectBox--InnerWrapperColor a.SelectboxCircle[data-title]"
      );
      if (!swatch) return;

      // Only fire on product pages.
      const path = window.location.pathname || "";
      const match = path.match(/\/products\/([^\/\?\#]+)/);
      if (!match) return;

      const title = swatch.dataset.title?.trim() || "";
      if (!title) return;

      const payload = {
        event: "product_color",
        data_title: title,
      };

      console.log("[GTag] product_color", payload);
      dataLayer.push(payload);
    }

    handleProductQuizCTAClick(event) {
      if (!dataLayer) return;

      const button = event.target?.closest?.(".productInfo--wraper .quiz--button");
      if (!button) return;

      // Only fire on product pages.
      const path = window.location.pathname || "";
      const match = path.match(/\/products\/([^\/\?\#]+)/);
      if (!match) return;

      const payload = { event: "product_quiz_CTA" };
      console.log("[GTag] product_quiz_CTA", payload);
      dataLayer.push(payload);
    }

    handleProductTabsClick(event) {
      if (!dataLayer) return;

      // Don't treat ingredient "view all" interactions as a tab click.
      if (event.target?.closest?.(".Product--ExpandButton")) return;

      const tab = event.target?.closest?.(".product__info-wrapper .swiper-slide");
      if (!tab) return;

      // Only fire on product pages.
      const path = window.location.pathname || "";
      const match = path.match(/\/products\/([^\/\?\#]+)/);
      if (!match) return;

      const tabTitle =
        tab.querySelector("span")?.textContent?.trim() ||
        tab.textContent?.trim() ||
        "";

      if (!tabTitle) return;

      const payload = {
        event: "product_tabs",
        tabs_title: tabTitle,
      };

      console.log("[GTag] product_tabs", payload);
      dataLayer.push(payload);
    }

    handleProductViewAllIngredientsClick(event) {
      if (!dataLayer) return;

      const button = event.target?.closest?.(".Product--ExpandButton");
      if (!button) return;

      // Only fire on product pages.
      const path = window.location.pathname || "";
      const match = path.match(/\/products\/([^\/\?\#]+)/);
      if (!match) return;

      const payload = { event: "product_tabs_view_all_ingredients" };
      console.log("[GTag] product_tabs_view_all_ingredients", payload);
      dataLayer.push(payload);
    }

    handleProductVideoClick(event) {
      if (!dataLayer) return;

      const videoSection = event.target?.closest?.("#section-firework-embed, .section-firework-embed, section-firework-embed");
      if (!videoSection) return;

      // Only fire on product pages.
      const path = window.location.pathname || "";
      const match = path.match(/\/products\/([^\/\?\#]+)/);
      if (!match) return;

      const payload = { event: "product_video_click" };
      console.log("[GTag] product_video_click", payload);
      dataLayer.push(payload);
    }

    handleProductReviewsTabClick(event) {
      if (!dataLayer) return;

      const tab = event.target?.closest?.(".oke-w-navBar-item");
      if (!tab) return;

      // Only fire on product pages.
      const path = window.location.pathname || "";
      const match = path.match(/\/products\/([^\/\?\#]+)/);
      if (!match) return;

      const payload = { event: "product_reviews_tab" };
      console.log("[GTag] product_reviews_tab", payload);
      dataLayer.push(payload);
    }

    handleProductRecommendedCarouselTabClick(event) {
      if (!dataLayer) return;

      const tab = event.target?.closest?.(".upsell__block .upsell__tab");
      if (!tab) return;

      // Only fire on product pages.
      const path = window.location.pathname || "";
      const match = path.match(/\/products\/([^\/\?\#]+)/);
      if (!match) return;

      const tabTitle = tab.textContent?.trim() || "";
      if (!tabTitle) return;

      const payload = {
        event: "product_recommended_carousel_tabs",
        tabs_title: tabTitle,
      };

      console.log("[GTag] product_recommended_carousel_tabs", payload);
      dataLayer.push(payload);
    }

    handleCartRecommendedCarouselArrowsClick(event) {
      if (!dataLayer) return;

      const button = event.target?.closest?.(".mini-cart__recommendations .swiperButton");
      if (!button) return;

      const payload = { event: "cart_recommended_carousel_arrows" };
      console.log("[GTag] cart_recommended_carousel_arrows", payload);
      dataLayer.push(payload);
    }

  domChanged() {
      const allIntersections = document.body.querySelectorAll('[data-gtag-intersection][data-observed="false"]');

      allIntersections.forEach((item) => {
        // Always use intersection observer so events re-fire when element scrolls back into view
        this.intersectionObserver.observe(item);
        item.dataset.observed = "true";
      });
    }
    
    processIntersectionElement(element) {
      const scriptElement = element.querySelector('script[type="application/ld+json"]');
      if (!scriptElement || !dataLayer) return;
      
      try {
        const data = JSON.parse(scriptElement.textContent);
        if (!data) return;

        dataLayer.push({ ecommerce: null });  
        dataLayer.push(data);
      } catch (e) {
        console.error("[GTag] Error parsing intersection element data:", e);
      }
    }
  
    observeDOMChanges() {
      const domMutationObserver = new MutationObserver((mutationsList, observer) => {
        // For each mutation record, we look for added nodes
        for (const mutation of mutationsList) {
          if (mutation.addedNodes.length) {
            // If there are added nodes, we call domChanged to check for new items
            this.domChanged();
          }
        }
      });
  
      // Start observing the document body for added nodes and subtree modifications
      domMutationObserver.observe(document.body, {
        childList: true, // Observe direct children
        subtree: true, // and all descendants
        attributes: false // Set to true if you also want to observe attribute changes
      });
    }
    
    handleIntersection(entries) {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
              this.processIntersectionElement(entry.target);
              // Do not unobserve – keep observing so event re-fires when user scrolls back into view
          }
        });
    }
}

new gTagEvents();
